import { useEffect, useState, useCallback } from "react";
import YouTube, { YouTubeEvent, YouTubePlayer } from "react-youtube";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import style from "./video-player-modal.module.css";
import classNames from "classnames";
import AppLoader from "frontend/loader/app-loader";
import { PlayIcon } from "frontend/icons/play";
import { PauseIcon } from "frontend/icons/pause";
import { ExpandIcon } from "frontend/icons/Expand";

const PLAYER_OPTIONS = {
  height: "385",
  width: "800",
  playerVars: {
    autoplay: 1,
    controls: 0,
    modestbranding: 1,
    preload: true,
    loading: "eager",
    rel: 0,
  },
};

function VideoPlayer({
  handleVideoEnded,
  videoId,
  setIsLoading,
}: {
  handleVideoEnded: () => void;
  videoId: string;
  setIsLoading: (isLoading: boolean) => void;
}) {
  const [player, setPlayer] = useState<YouTubePlayer | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  }, []);

  const handleReady = useCallback((event: YouTubeEvent) => {
    setPlayer(event.target);
    setDuration(event.target.getDuration());
    setIsLoading(false);
  }, []);

  const playerStates: { [key: number]: () => void } = {
    0: () => {
      handleVideoEnded();
      setIsPlaying(false);
    },
    1: () => {
      setIsLoading(false);
      setIsPlaying(true);
    },
    2: () => {
      setIsPlaying(false);
    },
  };

  const handleStateChange = (e: { target: any; data: number }) => {
    if (playerStates[e.data]) {
      playerStates[e.data]();
    }
  };

  useEffect(() => {
    const timeUpdateInterval = setInterval(() => {
      if (player && isPlaying) {
        setCurrentTime(player.getCurrentTime());
      }
    }, 1000);

    return () => clearInterval(timeUpdateInterval);
  }, [player, isPlaying]);

  const handlePlayPause = useCallback(() => {
    if (!player) return;

    if (isPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
  }, [player, isPlaying]);

  const handleSeek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!player) return;
      const newTime = parseFloat(e.target.value);
      player.seekTo(newTime, true);
      setCurrentTime(newTime);
    },
    [player]
  );

  const handleFullscreen = useCallback(() => {
    if (!player) return;
    player.getIframe().requestFullscreen();
  }, [player]);

  return (
    <>
      <div className={style.videoPlayer}>
        <YouTube
          videoId={videoId}
          opts={PLAYER_OPTIONS}
          onReady={handleReady}
          onStateChange={handleStateChange}
          className={style.video}
        />
        <div className={style.controls}>
          <div className={style.controlButton} onClick={handlePlayPause}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </div>
          <div className={style.progressContainer}>
            <input
              type="range"
              className={style.progressBar}
              min="0"
              max={duration}
              value={currentTime}
              onChange={handleSeek}
              style={{
                backgroundSize: `${(currentTime / duration) * 100}% 100%`,
              }}
            />
          </div>
          <div className={style.timeDisplay}>{formatTime(currentTime)}</div>
          <button className={classNames(style.controlButton, style.fullscreen)} onClick={handleFullscreen}>
            <ExpandIcon />
          </button>
        </div>
      </div>
    </>
  );
}

interface VideoPlayerModalProps {
  onDismiss: () => void;
  handleVideoEnded: () => void;
  videoId: string;
}

export default function VideoPlayerModal({ onDismiss, handleVideoEnded, videoId }: VideoPlayerModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  return (
    <>
      {isLoading && <AppLoader />}
      <div className={style.container} style={{ display: isLoading ? "none" : "flex" }}>
        <div className={style.closeIcon}>
          <CloseIcon
            onClick={onDismiss}
            color={"#FFFFFF"}
            style={{
              zIndex: 100000,
              width: 16,
              height: 16,
              marginTop: 7,
            }}
          />
        </div>
        <VideoPlayer handleVideoEnded={handleVideoEnded} videoId={videoId} setIsLoading={setIsLoading} />
      </div>
    </>
  );
}
